import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import OrgSwitcher from '../../components/OrgSwitcher';
import RoleHero from './RoleHero';
import { CustomizableDashboard } from '../../../../platform/widgets/CustomizableDashboard.jsx';
import { useDashboardViewer } from '../../../../platform/widgets/useDashboardViewer.js';
import { OEDashboardProvider } from '../../widgets/OEDashboardContext.jsx';
import { useOEDashboardData } from '../../widgets/useOEDashboardData.js';
import { RoleDashboardProvider } from '../../widgets/RoleDashboardContext.jsx';
import { useRoleDashboardData, scopeForLock } from '../../widgets/roleDashboardWidgets.jsx'; // registers the role widgets
import '../../widgets/oeDashboardWidgets.jsx'; // registers the OE widgets shared by every DREW dashboard

/**
 * Common frame of the access-level dashboards (B4, P3-10 close): OrgSwitcher + RoleHero + an
 * optional fixed banner (quick action, review prompt, Barometer) above a CustomizableDashboard
 * keyed per access level (P3-04 defaults, P3-19 persistence, P3-11 View As, P3-21 matrix).
 *
 *   <RoleDashboardPage dashboardKey={DASHBOARD_KEYS.drewFls} hero={{ icon, title, color, caption }}
 *                      conditions={{ title, scopeLabel, defaultFilter }} banner={(ctx) => <Card/>} />
 */
export default function RoleDashboardPage({ dashboardKey, hero, conditions = {}, banner = null }) {
  const viewer = useDashboardViewer();
  const role = useRoleDashboardData({ roleKey: dashboardKey });
  const scope = useMemo(() => scopeForLock(role.queryScope), [role.queryScope?.nwtId, role.queryScope?.deptId, role.queryScope?.siteId]);
  const oe = useOEDashboardData({ organization: role.activeOrg, scope, queryScope: role.queryScope });
  const roleValue = useMemo(
    () => ({ ...role, conditionsTitle: conditions.title, conditionsScopeLabel: typeof conditions.scopeLabel === 'function' ? conditions.scopeLabel(role) : conditions.scopeLabel, conditionsDefaultFilter: conditions.defaultFilter }),
    [role, conditions.title, conditions.scopeLabel, conditions.defaultFilter]
  );
  const HeroIcon = hero.icon;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <OrgSwitcher />
      </Box>
      <RoleHero icon={HeroIcon} title={hero.title} persona={role.persona} personaUser={role.personaUser} color={hero.color}>
        {typeof hero.caption === 'function' ? hero.caption(role) : hero.caption}
      </RoleHero>
      {banner ? banner(role) : null}
      <OEDashboardProvider value={oe.dashboardData}>
        <RoleDashboardProvider value={roleValue}>
          <CustomizableDashboard dashboardKey={dashboardKey} viewer={viewer} rowHeight={240} scopeOptions={oe.scopeOptions} />
        </RoleDashboardProvider>
      </OEDashboardProvider>
    </Box>
  );
}
